// src/components/EditAssessmentPopup.js
import React, { useState, useEffect } from 'react';
import './CreateAssessmentPopup.css';
import apiService from '../services/api';
import Snackbar from './Snackbar';

const EditAssessmentPopup = ({
  open,
  onClose,
  onSave,
  assessment,
  departmentsData,
  rolesData,
  categoriesMetadata,
  isMetadataLoading,
  managerDepartment,
}) => {
  const [formData, setFormData] = useState({
    id: '',
    department: '',
    role: '',
    created_by: sessionStorage.getItem('user_emailId'),
  });
  const [snackbar, setSnackbar] = useState({ message: '', type: 'success', isOpen: false });
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ message, type, isOpen: true });
  };
  const [categories, setCategories] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!assessment || !assessment.questions) {
      setCategories([]);
      setDeletedQuestionIds([]);
      return;
    }

    const grouped = {};
    assessment.questions.forEach((q) => {
      const catId = q.CategoryID;
      const catName = q.CategoryName || `Category ${catId}`;
      if (!grouped[catId]) {
        grouped[catId] = {
          id: catId,
          name: catName,
          description: q.CategoryDescription || '',
          questions: []
        };
      }
      grouped[catId].questions.push({
        id: q.ID,
        question: q.Question,
        description: q.Description || '',
        weightage: q.Weightage,
      });
    });

    setCategories(Object.values(grouped));
    setFormData({
      id: assessment.id,
      department: assessment.department,
      role: assessment.name,
      created_by: assessment.createdBy,
    });
    setDeletedQuestionIds([]); // reset
  }, [assessment]);

  const categoryOptions = categoriesMetadata?.data?.map((c) => ({
    id: c.CategoryID,
    name: c.CategoryName,
    description: c.Description,
  })) || [];

  const addCategory = () => {
    const nextId = categories.length > 0
      ? Math.max(...categories.map(c => c.id)) + 1
      : 1;
    setCategories([
      ...categories,
      { id: nextId, name: '', questions: [{ question: '', description: '', weightage: '' }] },
    ]);
  };

  const removeCategory = (idx) => {
    if (categories.length === 1) return;
    const removedCat = categories[idx];
    const removedIds = removedCat.questions
      .filter(q => q.id)
      .map(q => q.id);
    if (removedIds.length > 0) {
      setDeletedQuestionIds(prev => [...prev, ...removedIds]);
    }
    setCategories(categories.filter((_, i) => i !== idx));
  };

  const updateCategoryName = (idx, value) => {
    const selected = categoryOptions.find(o => o.name === value);
    const upd = [...categories];
    upd[idx].name = value;
    upd[idx].id = selected ? selected.id : null;
    upd[idx].description = selected ? selected.description : '';
    setCategories(upd);
  };

  const addQuestion = (catIdx) => {
    const upd = [...categories];
    upd[catIdx].questions.push({ question: '', description: '', weightage: '' });
    setCategories(upd);
  };

  const removeQuestion = (catIdx, qIdx) => {
    const upd = [...categories];
    const removedQ = upd[catIdx].questions[qIdx];
    if (removedQ.id) {
      setDeletedQuestionIds(prev => [...prev, removedQ.id]);
    }
    upd[catIdx].questions = upd[catIdx].questions.filter((_, i) => i !== qIdx);
    setCategories(upd);
  };

  const updateQuestion = (catIdx, qIdx, field, value) => {
    const upd = [...categories];
    if (field === 'weightage') {
      if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
        upd[catIdx].questions[qIdx][field] = value;
      }
    } else {
      upd[catIdx].questions[qIdx][field] = value;
    }
    setCategories(upd);
  };

  const calculateTotalWeightage = () => {
    return categories.reduce((tot, cat) => {
      return tot + cat.questions.reduce((sum, q) => {
        const w = parseInt(q.weightage);
        return sum + (isNaN(w) ? 0 : w);
      }, 0);
    }, 0);
  };
  const totalWeightage = calculateTotalWeightage();
  const isWeightageValid = totalWeightage === 100;

  const handleSubmit = async () => {
    if (!formData.department || !formData.role) {
      showSnackbar('Department and Role are required.', 'error');
      return;
    }

    const validCategories = categories.filter(cat => cat.id && cat.name);
    if (validCategories.length === 0) {
      showSnackbar('Category selection is mandatory.', 'error');
      return;
    }

    // Check if all categories are selected
    for (const cat of categories) {
      if (!cat.name || !cat.id) {
        showSnackbar('All category fields must be selected.', 'error');
        return;
      }
    }


    for (const cat of categories) {
      if (!cat.name || !cat.id) {
        showSnackbar('All category fields must be selected.', 'error');
        return;
      }

      if (cat.questions.length === 0) {
        showSnackbar('Each category must have at least one question.', 'error');
        return;
      }

      for (const q of cat.questions) {
        if (!q.question?.trim()) {
          showSnackbar('All question fields must be filled.', 'error');
          return;
        }

        const weight = parseInt(q.weightage);
        if (!q.weightage || isNaN(weight) || weight <= 0) {
          showSnackbar('Weightage must be a positive number greater than 0.', 'error');
          return;
        }
      }
    }

    const payload = {
      id: formData.id,
      department: formData.department.trim(),
      role: formData.role.trim(),
      created_by: formData.created_by,
      questions: validCategories
        .flatMap(cat =>
          cat.questions
            .filter(q => q.question.trim() && q.weightage && parseInt(q.weightage) > 0)
            .map(q => ({
              ID: q.id,
              CategoryID: cat.id,
              Question: q.question.trim(),
              Description: q.description?.trim() || '',
              Weightage: parseInt(q.weightage),
            }))
        ),
      deleted_questions: deletedQuestionIds
    };

    if (!isWeightageValid) {
      showSnackbar('Total weightage must be exactly 100%', 'error');
      return;
    }
    setIsSubmitting(true);

    try {
      await onSave(payload);
    } catch (error) {
      console.error('Error updating assessment:', error);
      showSnackbar('Failed to update assessment.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;
  if (isMetadataLoading || categoryOptions.length === 0) {
    return (
      <div className="ca-overlay">
        <div className="ca-modal">
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ca-overlay" onClick={onClose}>
        <div className="ca-modal" onClick={e => e.stopPropagation()}>
          {/* HEADER */}
          <div className="ca-header">
            <h2 className="ca-title">Edit Assessment</h2>
            <button className="ca-close-btn" onClick={onClose}>X</button>
          </div>

          {/* BODY */}
          <div className="ca-body">
            {/* BASIC INFO */}
            <div className="ca-section">
              <h3 className="ca-section-title">Basic Information</h3>
              <div className="ca-form-row">
                <div className="ca-form-group">
                  <label className="ca-label">Department <span className="ca-required">*</span></label>
                  <div
                    className="ca-select"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#1f2937',
                      cursor: 'not-allowed',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                    title="Department cannot be changed after creation"
                  >
                    {formData.department || '—'}
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Department cannot be changed after assessment is created
                  </small>
                </div>

                {/* <div className="ca-form-group">
                  <label className="ca-label">Role <span className="ca-required">*</span></label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                    className="ca-select"
                  >
                    <option value="">Select Role</option>
                    {rolesData?.data?.map(r => (
                      <option key={r.JobRoleID} value={r.RoleName}>{r.RoleName}</option>
                    ))}
                  </select>
                </div> */}
                <div className="ca-form-group">
                  <label className="ca-label">Role <span className="ca-required">*</span></label>
                  <div
                    className="ca-select"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#1f2937',
                      cursor: 'not-allowed',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                    title="Role cannot be changed after creation"
                  >
                    {formData.role || '—'}
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Role cannot be changed after assessment is created
                  </small>
                </div>
              </div>
            </div>

            {/* CATEGORIES */}
            <div className="ca-section">
              <div className="ca-section-title">
                Assessment Categories
                <span className={`ca-weightage-indicator ${isWeightageValid ? 'valid' : ''}`}>
                  Total Weightage: {totalWeightage}/100
                </span>
              </div>

              <div className="ca-categories">
                {categories.map((cat, catIdx) => (
                  <div key={catIdx} className="ca-category-card">
                    <div className="ca-category-header">
                      <div className="ca-category-select-wrapper">
                        <div className="ca-form-group">
                          <label className="ca-label" title={cat.description}>Category {catIdx + 1} <span className="ca-required">*</span></label>
                          <select
                            value={cat.name}
                            onChange={e => updateCategoryName(catIdx, e.target.value)}
                            className="ca-select"
                            title={cat.description || 'Select a category'}
                          >
                            <option value="">Select a category</option>
                            {categoryOptions.map(opt => (
                              <option key={opt.id} value={opt.name} title={opt.description}>{opt.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {categories.length > 1 && (
                        <button
                          className="ca-delete-category-btn"
                          onClick={() => removeCategory(catIdx)}
                          title="Remove Category"
                        >
                          X
                        </button>
                      )}
                    </div>

                    <div className="ca-questions">
                      {cat.questions.map((q, qIdx) => (
                        <div key={qIdx} className="ca-question-row">
                          <div className="ca-question-input-wrapper">
                            <label className="ca-label">Question {qIdx + 1} <span className="ca-required">*</span></label>
                            <textarea
                              value={q.question}
                              onChange={e => updateQuestion(catIdx, qIdx, 'question', e.target.value)}
                              placeholder="Enter assessment question..."
                              className="ca-textarea"
                              rows={2}
                            />
                          </div>
                          {/* Description input below question input */}
                          <div className="ca-description-input-wrapper" style={{ marginTop: '8px' }}>
                            <label className="ca-label">Description <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>(optional)</span></label>
                            <textarea
                              value={q.description || ''}
                              onChange={e => updateQuestion(catIdx, qIdx, 'description', e.target.value)}
                              placeholder="Enter question description..."
                              className="ca-textarea"
                              rows={2}
                            />
                          </div>
                          <div className="ca-weightage-input-wrapper">
                            <label className="ca-label">Weightage <span className="ca-required">*</span></label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={q.weightage}
                              onChange={e => {
                                const v = e.target.value;
                                if (v === '' || /^\d+$/.test(v)) {
                                  updateQuestion(catIdx, qIdx, 'weightage', v);
                                }
                              }}
                              placeholder="0-100"
                              className="ca-input"
                            />
                          </div>
                          {cat.questions.length > 1 && (
                            <button
                              className="ca-delete-question-btn"
                              onClick={() => removeQuestion(catIdx, qIdx)}
                              title="Remove Question"
                            >
                              X
                            </button>
                          )}
                        </div>
                      ))}

                      <button
                        className="ca-add-btn"
                        onClick={() => addQuestion(catIdx)}
                      >
                        + Add Question
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="ca-add-btn ca-add-category-btn" onClick={addCategory}>
                + Add Category
              </button>
            </div>
          </div>

          {/* FOOTER */}
          <div className="ca-footer">
            <div className="ca-footer-left">
              {!isWeightageValid && (
                <span className="ca-warning-text">Warning: Total weightage must be 100%</span>
              )}
            </div>
            <div className="ca-footer-actions">
              <button className="ca-btn ca-btn-cancel" onClick={onClose}>Cancel</button>
              {/* <button
                className="ca-btn ca-btn-submit"
                onClick={handleSubmit}
                disabled={!isWeightageValid || !formData.department || !formData.role}
              >
                Update Assessment
              </button> */}
              <button
                className="ca-btn ca-btn-submit"
                onClick={handleSubmit}
                disabled={!isWeightageValid || !formData.department || !formData.role || isSubmitting}
              >
                {isSubmitting ? 'Updating assessment...' : 'Update Assessment'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {snackbar.isOpen && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          duration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, isOpen: false }))}
        />
      )}
    </>
  );
};

export default EditAssessmentPopup;